class GuidedCourse extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const tabs = [...this.querySelectorAll('[data-pv-course-tab]')];
    const panels = [...this.querySelectorAll('[data-pv-course-panel]')];
    const weeks = [...this.querySelectorAll('.pv4-week-rail > li')];
    const tabList = this.querySelector('[data-pv-course-tabs]');

    if (!tabs.length || tabs.length !== panels.length) return;

    this.dataset.enhanced = 'true';
    tabList?.setAttribute('role', 'tablist');

    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panels[index].id);
    });

    panels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabs[index].id);
    });

    const activate = (index, focus = false) => {
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });

      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      const progress = Number.parseInt(tabs[index]?.dataset.pvCourseProgress || '0', 10);
      weeks.forEach((week, weekIndex) => {
        const complete = progress >= weeks.length ? weekIndex < progress : weekIndex < Math.max(0, progress - 1);
        const active = progress > 0 && progress < weeks.length && weekIndex === progress - 1;
        week.classList.toggle('is-complete', complete);
        week.classList.toggle('is-active', active);
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activate(nextIndex, true);
        },
        { signal }
      );
    });

    activate(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }
}

if (!customElements.get('guided-course')) {
  customElements.define('guided-course', GuidedCourse);
}

class PillowLab extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const hotspots = [...this.querySelectorAll('[data-pv-pillow-hotspot]')];
    const details = [...this.querySelectorAll('[data-pv-pillow-detail]')];

    if (!hotspots.length || hotspots.length !== details.length) return;

    const activate = (index) => {
      hotspots.forEach((hotspot, hotspotIndex) => {
        const active = hotspotIndex === index;
        hotspot.classList.toggle('is-active', active);
        hotspot.setAttribute('aria-pressed', String(active));
      });

      details.forEach((detail, detailIndex) => {
        detail.classList.toggle('is-active', detailIndex === index);
      });
    };

    hotspots.forEach((hotspot, index) => {
      hotspot.addEventListener('click', () => activate(index), { signal });
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }
}

if (!customElements.get('pillow-lab')) {
  customElements.define('pillow-lab', PillowLab);
}

const galleryControllers = new WeakMap();

function bindProductGallery(details) {
  if (!(details instanceof HTMLDetailsElement) || galleryControllers.has(details)) return;

  const abortController = new AbortController();
  galleryControllers.set(details, abortController);
  details.addEventListener(
    'toggle',
    () => {
      if (!details.open) return;
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    },
    { signal: abortController.signal }
  );
}

function initialize(root = document) {
  root.querySelectorAll('[data-pv4-product-gallery]').forEach(bindProductGallery);
}

initialize();

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('a[href="#app-erleben"]') : null;
  if (!trigger) return;

  const target = document.querySelector('[data-pv-app-experience]');
  if (!target) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  history.replaceState(null, '', '#app-erleben');
});

document.addEventListener('shopify:section:load', (event) => initialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-pv4-product-gallery]').forEach((details) => {
    galleryControllers.get(details)?.abort();
    galleryControllers.delete(details);
  });
});

